import "@/App.css";
import "@/i18n"; // Initialize i18n (must happen before any t() call)
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { PrefsProvider } from "@/contexts/PrefsContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import RedirectIfAuthed from "@/components/RedirectIfAuthed";
import RequireAdmin from "@/components/RequireAdmin";
import CommandPalette from "@/components/CommandPalette";
import InstallPrompt from "@/components/pwa/InstallPrompt";
import AdminShell from "@/components/AdminShell";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import Settings from "@/pages/Settings";
import DealWizard from "@/pages/DealWizard";
import DealDetail from "@/pages/DealDetail";
import Copilot from "@/pages/Copilot";
import Portfolio from "@/pages/Portfolio";
import Compare from "@/pages/Compare";
import Reports from "@/pages/Reports";
import Pipeline from "@/pages/Pipeline";
import Discovery from "@/pages/Discovery";
import SyndicationInvite from "@/pages/SyndicationInvite";
import PartnershipDetail from "@/pages/PartnershipDetail";
import InvestorPortal from "@/pages/InvestorPortal";
import AllPartnerships from "@/pages/AllPartnerships";
import Manage from "@/pages/Manage";
import PropertyHub from "@/pages/PropertyHub";
import ManageMessages from "@/pages/ManageMessages";
import TenantAccept from "@/pages/TenantAccept";
import TenantHome from "@/pages/TenantHome";
import TenantLease from "@/pages/TenantLease";
import TenantMaintenance from "@/pages/TenantMaintenance";
import TenantPayments from "@/pages/TenantPayments";
import TenantProfile from "@/pages/TenantProfile";
import { RequireTenant } from "@/components/RoleGuards";
import GoogleCallback from "@/pages/GoogleCallback";
import AuthCallback from "@/pages/AuthCallback";
import AdminHome from "@/pages/admin/AdminHome";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminUserDetail from "@/pages/admin/AdminUserDetail";
import AdminDeals from "@/pages/admin/AdminDeals";
import AdminMarket from "@/pages/admin/AdminMarket";
import AdminIntegrations from "@/pages/admin/AdminIntegrations";
import AdminAuditLogPage from "@/pages/admin/AdminAuditLog";
import AdminViralAttribution from "@/pages/admin/AdminViralAttribution";
import AdminSystemHealthPage from "@/pages/admin/AdminSystemHealth";
import AdminAIUsagePage from "@/pages/admin/AdminAIUsage";
import AdminProperties from "@/pages/admin/AdminProperties";
import AdminLeases from "@/pages/admin/AdminLeases";
import AdminMaintenance from "@/pages/admin/AdminMaintenance";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminEmail from "@/pages/admin/AdminEmail";
import ResetPassword from "@/pages/ResetPassword";
import AdminFeatureFlags from "@/pages/admin/AdminFeatureFlags";
import AdminAnnouncements from "@/pages/admin/AdminAnnouncements";
// Phase 36 Batch T — Admin Analytics
import AdminAnalyticsUsage from "@/pages/admin/AdminAnalyticsUsage";
import AdminAnalyticsFunnel from "@/pages/admin/AdminAnalyticsFunnel";
import AdminAnalyticsGeo from "@/pages/admin/AdminAnalyticsGeo";
import AdminEvents from "@/pages/admin/AdminEvents";
import {
  AdminCopilot,
  AdminAiCalls,
  AdminFxRates,
  AdminTaxRules,
  AdminAuditLog,
  AdminTenants,
} from "@/pages/admin/AdminSimplePages";

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PrefsProvider>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route
            path="/login"
            element={
              <RedirectIfAuthed>
                <Login />
              </RedirectIfAuthed>
            }
          />
          <Route
            path="/signup"
            element={
              <RedirectIfAuthed>
                <Signup />
              </RedirectIfAuthed>
            }
          />
          <Route path="/auth/google/callback" element={<GoogleCallback />} />
          {/* Phase 36 Batch S — Emergent-managed Google Sign-In return URL */}
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/deals/new"
            element={
              <ProtectedRoute>
                <DealWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/deals/:id"
            element={
              <ProtectedRoute>
                <DealDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/deals/:id/edit"
            element={
              <ProtectedRoute>
                <DealWizard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/copilot"
            element={
              <ProtectedRoute>
                <Copilot />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portfolio"
            element={
              <ProtectedRoute>
                <Portfolio />
              </ProtectedRoute>
            }
          />
          {/* Phase 29 — Multi-property compare + Excel/PDF export */}
          <Route
            path="/compare"
            element={
              <ProtectedRoute>
                <Compare />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pipeline"
            element={
              <ProtectedRoute>
                <Pipeline />
              </ProtectedRoute>
            }
          />
          {/* Phase 36 Batch AM — Property Discovery V1 */}
          <Route
            path="/discovery"
            element={
              <ProtectedRoute>
                <Discovery />
              </ProtectedRoute>
            }
          />
          {/* Batch G + Hotfix — Intelligence has been folded into the
              Portfolio page (view=prices is the headline value). This
              soft-redirect catches any bookmarks. */}
          <Route
            path="/intelligence"
            element={<Navigate to="/portfolio?view=prices" replace />}
          />
          {/* Phase 34a REPLACE — Syndication */}
          <Route
            path="/syndication/invite/:token"
            element={
              <ProtectedRoute>
                <SyndicationInvite />
              </ProtectedRoute>
            }
          />
          <Route
            path="/syndication/partnerships/:id"
            element={
              <ProtectedRoute>
                <PartnershipDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/partnerships"
            element={
              <ProtectedRoute>
                <AllPartnerships />
              </ProtectedRoute>
            }
          />
          <Route
            path="/investor"
            element={
              <ProtectedRoute>
                <InvestorPortal />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manage"
            element={
              <ProtectedRoute>
                <Manage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manage/properties/:id"
            element={
              <ProtectedRoute>
                <PropertyHub />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manage/messages"
            element={
              <ProtectedRoute>
                <ManageMessages />
              </ProtectedRoute>
            }
          />
          {/* Phase 8b — Tenant portal routes */}
          <Route path="/tenant/accept" element={<TenantAccept />} />
          {/* Phase 26 — public password reset route (entry point from email link) */}
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/tenant"
            element={<RequireTenant><TenantHome /></RequireTenant>}
          />
          <Route
            path="/tenant/lease"
            element={<RequireTenant><TenantLease /></RequireTenant>}
          />
          <Route
            path="/tenant/maintenance"
            element={<RequireTenant><TenantMaintenance /></RequireTenant>}
          />
          <Route
            path="/tenant/payments"
            element={<RequireTenant><TenantPayments /></RequireTenant>}
          />
          <Route
            path="/tenant/profile"
            element={<RequireTenant><TenantProfile /></RequireTenant>}
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          {/* Admin */}
          <Route
            path="/admin"
            element={
              <RequireAdmin>
                <AdminShell />
              </RequireAdmin>
            }
          >
            <Route index element={<AdminHome />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:id" element={<AdminUserDetail />} />
            <Route path="properties" element={<AdminProperties />} />
            <Route path="deals" element={<AdminDeals />} />
            <Route path="market" element={<AdminMarket />} />
            <Route path="integrations" element={<AdminIntegrations />} />
            <Route path="audit-log" element={<AdminAuditLogPage />} />
            <Route path="health" element={<AdminSystemHealthPage />} />
            <Route path="ai-calls" element={<AdminAIUsagePage />} />
            <Route path="copilot" element={<AdminCopilot />} />
            <Route path="ai-calls-legacy" element={<AdminAiCalls />} />
            <Route path="fx-rates" element={<AdminFxRates />} />
            <Route path="tax-rules" element={<AdminTaxRules />} />
            <Route path="tenants" element={<AdminTenants />} />
            <Route path="leases" element={<AdminLeases />} />
            <Route path="maintenance" element={<AdminMaintenance />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="email" element={<AdminEmail />} />
            <Route path="feature-flags" element={<AdminFeatureFlags />} />
            <Route path="announcements" element={<AdminAnnouncements />} />
            {/* Phase 36 Batch T — Analytics surfaces */}
            <Route path="analytics/usage" element={<AdminAnalyticsUsage />} />
            <Route path="analytics/funnel" element={<AdminAnalyticsFunnel />} />
            <Route path="analytics/geo" element={<AdminAnalyticsGeo />} />
            <Route path="events" element={<AdminEvents />} />
            <Route path="audit-log" element={<AdminAuditLog />} />
            <Route path="viral-attribution" element={<AdminViralAttribution />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster />
        {/* Phase 33 — ⌘+K command palette (renders nothing until opened) */}
        <CommandPalette />
        {/* Phase 36 Batch V — PWA install prompt (chip, bottom-right, auto-hides in standalone) */}
        <InstallPrompt />
        </PrefsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
